import {
  Box,
  BoxProps,
  Heading,
  HeadingProps,
  Link,
  ListItem,
  ListProps,
  StackProps,
  Text,
  UnorderedList,
  VStack,
} from '@chakra-ui/react';

function Title(props: HeadingProps) {
  return <Heading as="h1" {...props} />;
}

function Section(props: StackProps) {
  return <VStack as="section" align="stretch" spacing={4} {...props} />;
}

function SectionTitle(props: HeadingProps) {
  return <Heading as="h2" fontSize="xl" {...props} />;
}

function SubsectionTitle(props: HeadingProps) {
  return <Heading as="h3" fontSize="lg" {...props} />;
}

function SubsectionTextList(props: ListProps) {
  return <UnorderedList listStyleType='"\2713"' ps={8} {...props} />;
}

function NgExampleList({ children, ...restProps }: BoxProps) {
  return (
    <Box ps={5}>
      <Box {...restProps} borderColor="gray.600" borderWidth={1} p={2}>
        <Text>【NG例】</Text>
        <UnorderedList ms={8}>{children}</UnorderedList>
      </Box>
    </Box>
  );
}

function NgCheckList(props: ListProps) {
  return <UnorderedList {...props} listStyleType='"\25A1"' />;
}

/**
 * 知的財産取扱いガイドライン本文
 */
export default function IntellectualPropertyGuideline() {
  return (
    <VStack
      align="stretch"
      spacing={16}
      lineHeight={1.8}
      p={2}
      wordBreak="break-all"
    >
      <Title>Antaa Slide知的財産取扱いガイドライン</Title>

      <Section>
        <Text>
          Antaa
          Slideは医師同士がスライドを互いに共有し、知識をつなぐことを目指して生まれました。
          <br />
          学会や研究レポート・勉強会などの資料を共有し、互いに教え合い、共に学べる場所を提供しています。
          <br />
          投稿いただくスライドには、投稿者以外の第三者が作成した文章、図、画像が含まれることがありますが、このガイドラインでは著作権を中心とした知的財産に関する一般的な注意事項を記載させていただきました。
          この「Antaa
          Slide知的財産取扱いガイドライン」は、皆様に安心して投稿、閲覧いただくために作られたガイドラインです。このガイドラインは必要に応じて更新されます。必ず目を通してご理解、ご承諾の上、ご利用ください。
        </Text>
      </Section>

      <Section>
        <SectionTitle>
          １　著作権法上の「引用」として認められる方法により記載しましょう
        </SectionTitle>
        <Text>
          学術論文、雑誌等から文章、図、画像等の第三者の著作物を利用する場合、著作権者や出版権者等からの許諾を得ない限り、著作権法上の「引用」として認められた方法でなければ違法な利用となってしまいます。許諾を得ていない著作物を利用する際には、著作権法の引用のルールに照らして、以下の事項に留意してください。
        </Text>

        <SubsectionTitle>
          （１）　公表されている著作物を使いましょう
        </SubsectionTitle>
        <SubsectionTextList>
          <ListItem>
            著作権法上の引用として第三者の著作物を利用する場合、対象となる著作物が公表（出版や自由にアクセスできるインターネット上で掲載される等、公衆に提示された場合）されていることが必要です。
          </ListItem>
          <ListItem>
            世間一般に公開されたり、発行されたりしていない著作物はスライドに利用しないようにしましょう。
          </ListItem>
        </SubsectionTextList>
        <NgExampleList>
          <ListItem>
            研究機関が一般公開する前の論文に含まれた記述を掲載していた。
          </ListItem>
        </NgExampleList>

        <SubsectionTitle>
          （２）　自身が作成した部分と引用した部分が明瞭に区別できるようにしましょう
        </SubsectionTitle>
        <SubsectionTextList>
          <ListItem>
            著作権法上の引用として第三者の著作物を利用する場合、自身が創作した部分と第三者の著作物を引用した部分が明瞭に区別できることが必要です。
          </ListItem>
          <ListItem>
            第三者の著作物を掲載する際は、出所と著作者名の明示を行いましょう。
          </ListItem>
          <ListItem>
            明瞭に区別できるようにするため、カギ括弧で囲む、引用部分の前後を1行あける、1字下げる等の工夫をして、閲覧者に対して、あたかも自分が創作したと誤認させないようにしましょう。
          </ListItem>
        </SubsectionTextList>
        <NgExampleList>
          <ListItem>
            第三者の研究結果の図表を出所、著作者名も記載せず利用していた。
          </ListItem>
          <ListItem>
            第三者の文献にあった記述を、「　」も付けず、長文をそのまま利用していた。
          </ListItem>
        </NgExampleList>

        <SubsectionTitle>
          （３）　「主従関係」があるようにしましょう
        </SubsectionTitle>
        <SubsectionTextList>
          <ListItem>
            著作権法上の引用として第三者の著作物を利用する場合、自分が創作した部分を「主」とし、引用する部分を「従」とした「主従関係」が必要です。主従関係がない場合は、無断転載と非難されてしまうことになります。
          </ListItem>
          <ListItem>
            この主従関係は、文量的な観点だけでなく、質的な観点からも判断されるため、スライド上の比率だけではなく、記述されている内容面でも留意が必要です。
          </ListItem>
        </SubsectionTextList>
        <NgExampleList>
          <ListItem>全スライドの8割が雑誌記事の記述だった。</ListItem>
        </NgExampleList>

        <SubsectionTitle>
          （４）　出所表示等の公正な慣行にも注意しましょう
        </SubsectionTitle>
        <SubsectionTextList>
          <ListItem>
            著作物も種類は様々です。著作物の利用は、国、時代、業界、媒体等により引用の慣行も異なります。引用する際には、「その著作物」に関して慣行となっている引用方法にも注意しましょう。
          </ListItem>
          <ListItem>
            例えば、医療業界の著作物を引用する際は、日本医書出版協会様の公表している注意事項（
            <Link
              href="https://www.medbooks.or.jp/copyright/forauthor/quot.php"
              isExternal
            >
              https://www.medbooks.or.jp/copyright/forauthor/quot.php
            </Link>
            ）を参考にするとよいといえます。
          </ListItem>
          <ListItem>
            医療業界は国際的にも著作権の扱いは整理されている傾向にあり、例えば出所表示については著作権者のみでなく出版社を記載する必要があることや、利用場面に応じては、日本の著作権法違反を構成しないにも、的確な記載が求められるときがある業界（例えば「バンクーバー方式」「ハーバード方式」等）であることは注意しましょう。
          </ListItem>
        </SubsectionTextList>
        <NgExampleList>
          <ListItem>
            医学雑誌の論文をスライド上で利用した際、出所として、著者名は記載したものの、出版社を記載していなかった。
          </ListItem>
        </NgExampleList>

        <SubsectionTitle>
          （５）　引用目的に応じた範囲の利用を心がけましょう
        </SubsectionTitle>
        <SubsectionTextList>
          <ListItem>
            引用により利用できる範囲も、利用目的に照らして正当な範囲であることが必要です。
          </ListItem>
        </SubsectionTextList>
        <NgExampleList>
          <ListItem>
            雑誌の中から1行程度の文章を引用すれば足りるにもかかわらず、スライド上に雑誌の１ページを丸ごと記載した。
          </ListItem>
        </NgExampleList>
      </Section>

      <Section>
        <SectionTitle>
          ２　著作権法以外に、「引用元の特別ルール」も注意してください
        </SectionTitle>
        <Text>
          実は、著作権法に遵守した引用だからといっても安心はできません。
          <br />
          論文、書籍、電子ジャーナル等には、利用規約や購読時の契約書等で、著作権法に加えて、各社各様の制限が別途定められている場合があります。
          <br />
          投稿者自身が過去に書いた文書であっても出版社との契約によっては再利用の際には出版社の許可が必要とされていることもあります。
          <br />
          特に、海外の電子ジャーナル等を引用する際には、現地の著作権法のみならず、外国語で記述された規約も確認しなければ安心して引用ができない場合があるので、ご注意ください。
        </Text>
      </Section>

      <Section>
        <SectionTitle>
          ３　著作権法上の引用に当たらない場合には、適切な許諾を取得しましょう
        </SectionTitle>
        <Text>
          著作権法の引用といえない場合、著作権者等からの利用許諾が必要となります。
          <br />
          特に、書籍の場合は著作者のみならず、出版社からも許諾を得る必要があるためご注意ください。
        </Text>
      </Section>

      <Section>
        <SectionTitle>４　さいごに</SectionTitle>
        <Text>
          著作権法上の引用として適切かどうか、引用元のルールに違反していないかという判断は、利用される著作物やその利用態様により異なります。
          <br />
          当社としても、参考までにチェックリストを掲載いたしますが、あくまでも参考のものとなります。チェックリストに該当しないから違反がないというわけではなく、ご不明な場合や判断に悩んだ場合は、出版社、著作権者、法律専門家等に適宜ご確認いただけますよう、お願いいたします。
        </Text>
        <Box>
          <Text>
            【参考】NGチェックリスト
            <br />
            ※一つでも懸念される事項がある場合は、NGとなる可能性がありますので、ご注意ください。
          </Text>
          <NgCheckList>
            <ListItem>
              出版や一般公開されていない著作物ではありませんか？
            </ListItem>
            <ListItem>
              第三者の文章は「　」で囲み、図表は出所を明示する等、自身の著作物と引用しようとする著作物が明確に区別することを忘れていませんか？
            </ListItem>
            <ListItem>
              スライドの全体や各ページの内容を見たとき、質や量の観点から、特定の第三者の著作物が「主」の関係になっていませんか？
            </ListItem>
            <ListItem>
              著作物の種類や内容に応じた一般的な引用の記載や出所表示を忘れていませんか？
            </ListItem>
            <ListItem>
              引用目的に照らして、利用しようとする著作物の文量や割合が過度なものとなっていませんか？
            </ListItem>
            <ListItem>
              著作権法上は適法な引用であっても、引用元となる媒体の出版社やサービスのルール上、無断利用が禁止されていませんか？
            </ListItem>
          </NgCheckList>
        </Box>
      </Section>

      <Section>
        <Text>2022年6月15日 策定</Text>
      </Section>
    </VStack>
  );
}
